import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { CircleX, TriangleAlert } from 'lucide-react';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { pcmpProposalDetailQueryOptions } from '../../api/pcmpProposals.api';
import {
  PCMP_MKT_COMMENT_MAX,
  PCMP_MKT_REJECTION_TYPE_COPY,
  PCMP_MKT_SCHOOL_REJECT_REASON_LABELS,
} from '../../constants/pcmpProposal.constants';
import { useRejectPcmpProposal } from '../../hooks/useRejectPcmpProposal';
import {
  PCMP_MKT_REJECTION_TYPES,
  PCMP_MKT_SCHOOL_REJECT_REASONS,
} from '../../types/pcmpProposal.types';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpCommentField } from './PcmpCommentField';

const rejectFormSchema = z
  .object({
    type: z.enum(PCMP_MKT_REJECTION_TYPES),
    schoolReason: z.enum(PCMP_MKT_SCHOOL_REJECT_REASONS).optional(),
    comment: z.string().trim().min(1, 'Rejection comment is required.').max(PCMP_MKT_COMMENT_MAX),
  })
  .superRefine((values, ctx) => {
    if (values.type !== 'SCHOOL_WITH_TEACHER') return;
    if (!values.schoolReason) {
      ctx.addIssue({
        code: 'custom',
        path: ['schoolReason'],
        message: 'Select a reason for the school rejection.',
      });
    } else if (values.schoolReason === 'INFORMATION_CORRECTION') {
      ctx.addIssue({
        code: 'custom',
        path: ['schoolReason'],
        message:
          'Information Correction is not grounds for rejection. Reject the school only when it is not enlisted.',
      });
    }
  });

type RejectFormValues = z.infer<typeof rejectFormSchema>;

export interface PcmpMarketingRejectTarget {
  proposalId: number;
  refNo: string;
  /** From the details' capabilities; left out, it is read from the proposal. */
  canRejectInstitute?: boolean | undefined;
}

interface PcmpMarketingRejectDialogProps {
  target: PcmpMarketingRejectTarget;
  onClose: () => void;
}

interface OptionRowProps {
  id: string;
  value: string;
  label: string;
  hint?: string | undefined;
  disabled?: boolean;
}

function OptionRow({ id, value, label, hint, disabled }: OptionRowProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border px-3 py-2.5',
        disabled && 'bg-muted/40',
      )}
    >
      <RadioGroupItem id={id} value={value} disabled={disabled} className="mt-0.5" />
      <Label
        htmlFor={id}
        className={cn(
          'flex flex-col items-start gap-0.5 font-normal',
          disabled && 'text-muted-foreground',
        )}
      >
        <span className="font-medium">{label}</span>
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </Label>
    </div>
  );
}

/** Legacy MktReject: the teacher only, or the school with it. The reject is final. */
export function PcmpMarketingRejectDialog({ target, onClose }: PcmpMarketingRejectDialogProps) {
  const form = useForm<RejectFormValues>({
    resolver: zodResolver(rejectFormSchema),
    defaultValues: { type: 'TEACHER', comment: '' },
  });
  const { mutate, isPending, error } = useRejectPcmpProposal(target.proposalId, onClose);
  const type = useWatch({ control: form.control, name: 'type' });

  // A queue row does not carry it, so ask the proposal.
  const needsDetail = target.canRejectInstitute === undefined;
  const detail = useQuery({
    ...pcmpProposalDetailQueryOptions(target.proposalId),
    enabled: needsDetail,
  });
  const canRejectInstitute =
    target.canRejectInstitute ?? detail.data?.capabilities.canRejectInstitute ?? false;

  let schoolHint: string = PCMP_MKT_REJECTION_TYPE_COPY.SCHOOL_WITH_TEACHER.hint;
  if (!canRejectInstitute) {
    if (needsDetail && detail.isLoading) {
      schoolHint = 'Checking whether the school can be rejected...';
    } else if (needsDetail && detail.isError) {
      schoolHint = 'Could not check whether the school can be rejected.';
    } else {
      schoolHint = 'Not available: the institute has other proposals under this project.';
    }
  }

  const onSubmit = form.handleSubmit((values) =>
    mutate({
      type: values.type,
      schoolReason: values.type === 'SCHOOL_WITH_TEACHER' ? values.schoolReason : undefined,
      comment: values.comment,
    }),
  );

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Reject Proposal"
        description={target.refNo}
        icon={CircleX}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Reject"
        submitVariant="destructive"
        pendingLabel="Rejecting..."
      >
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>A rejection is final. The proposal cannot be approved afterwards.</span>
        </div>

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Rejection type <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    if (value !== 'SCHOOL_WITH_TEACHER') form.resetField('schoolReason');
                  }}
                >
                  <OptionRow
                    id="pcmp-reject-teacher"
                    value="TEACHER"
                    label={PCMP_MKT_REJECTION_TYPE_COPY.TEACHER.label}
                    hint={PCMP_MKT_REJECTION_TYPE_COPY.TEACHER.hint}
                  />
                  <OptionRow
                    id="pcmp-reject-school"
                    value="SCHOOL_WITH_TEACHER"
                    label={PCMP_MKT_REJECTION_TYPE_COPY.SCHOOL_WITH_TEACHER.label}
                    hint={schoolHint}
                    disabled={!canRejectInstitute}
                  />
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {type === 'SCHOOL_WITH_TEACHER' ? (
          <FormField
            control={form.control}
            name="schoolReason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  School rejection reason <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <RadioGroup value={field.value ?? ''} onValueChange={field.onChange}>
                    {PCMP_MKT_SCHOOL_REJECT_REASONS.map((reason) => (
                      <OptionRow
                        key={reason}
                        id={`pcmp-reject-reason-${reason}`}
                        value={reason}
                        label={PCMP_MKT_SCHOOL_REJECT_REASON_LABELS[reason]}
                        hint={
                          reason === 'INFORMATION_CORRECTION'
                            ? 'Not grounds for rejection. Edit the proposal instead.'
                            : undefined
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}

        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          required
          placeholder="Why is this proposal rejected?"
        />
      </PcmpEditDialog>
    </Form>
  );
}
